import { API, graphqlOperation } from "aws-amplify";
import {
  updateSeal,
  createSeal as createSealRequest,
} from "../../../graphql/mutations";
import { performUpdateInbound } from "../../../graphql/inbound/inboundCRUD";

import { CarrierType, SealStatus } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import {
  getAlignedTrackCarLocation,
  isTrack,
} from "../../../utils/locationUtils";
import { normalizeRailCarNumber } from "../../../utils/railCarUtils";

const modifySeal = async ({ sealId, updatedSeal, notify }) => {
  try {
    if (!sealId) {
      throw new Error("Required fields are missing");
    }
    const sealInput = {
      id: sealId,
      ...updatedSeal,
    };
    const result = await API.graphql(
      graphqlOperation(updateSeal, { input: sealInput })
    );
    return result.data.updateSeal;
  } catch (error) {
    logError("ModifySeal", error, notify);
    throw error;
  }
};

const modifySealAction = async ({
  sealId,
  barcode,
  createdBy,
  dispatch,
  inboundShipment,
  updateSealData,
  actionType,
  actionText,
  notify,
}) => {
  try {
    const seal = await modifySeal({
      notify,
      sealId,
      updatedSeal: updateSealData,
    });
    const newHistory = {
      date: new Date().toISOString(),
      by: createdBy,
      type: actionType,
      text: `Seal with barcode ${barcode} with ID ${sealId} was ${actionText}`,
    };
    const updatedHistory = inboundShipment.history
      ? [...inboundShipment.history, newHistory]
      : [newHistory];

    const inboundShipmentToUpdate = {
      id: inboundShipment.id,
      history: updatedHistory,
    };
    await performUpdateInbound(inboundShipmentToUpdate);

    const inboundShipmentToDispatch = {
      ...inboundShipment,
      history: updatedHistory,
      seals: {
        ...inboundShipment.seals,
        items: inboundShipment.seals.items.map((item) =>
          item.id === sealId ? seal : item
        ),
      },
    };
    dispatch({
      type: "SET_INBOUND_SHIPMENT",
      payload: inboundShipmentToDispatch,
    });
    return inboundShipmentToDispatch;
  } catch (error) {
    logError("modifySealAction", error, notify);
    throw error;
  }
};

const revalidateSeal = async (params) => {
  return modifySealAction({
    ...params,
    updateSealData: { reValidated: true },
    actionType: "SEAL_REVALIDATED",
    actionText: "revalidated",
  });
};

const breakSeal = async (params) => {
  return modifySealAction({
    ...params,
    updateSealData: {
      brokenAt: new Date().toISOString(),
      brokenBy: params.createdBy,
    },
    actionType: "SEAL_BROKEN",
    actionText: "broken",
  });
};

const deActivateSeal = async (params) => {
  return modifySealAction({
    ...params,
    updateSealData: { active: false },
    actionType: "SEAL_DEACTIVATED",
    actionText: "deactivated",
  });
};

const reActivateSeal = async (params) => {
  return modifySealAction({
    ...params,
    updateSealData: { active: true },
    actionType: "SEAL_REACTIVATED",
    actionText: "reactivated",
  });
};

const createSeal = async ({
  currentShipment,
  barcode,
  createdBy,
  note = "",
  status,
}) => {
  const sealInput = {
    barcode,
    description: note,
    referenceId: currentShipment.id,
    owner: currentShipment.owner,
    groups: currentShipment.groups,
    reloadYardId: currentShipment.reloadYardId,
    accountId: currentShipment.accountId,
    accountName: currentShipment.accountName,
    referenceName: "InboundShipment",
    validated: true,
    active: true,
    status,
    createdBy,
  };

  const result = await API.graphql(
    graphqlOperation(createSealRequest, { input: sealInput })
  );
  return result.data.createSeal;
};

const createNewSeal = async ({
  barcode,
  createdBy,
  dispatch,
  note,
  inboundShipment,
  notify,
}) => {
  try {
    const seal = await createSeal({
      currentShipment: inboundShipment,
      createdBy,
      barcode,
      note,
      status: SealStatus.NEW,
    });
    const newHistory = {
      date: new Date().toISOString(),
      by: createdBy,
      type: "SEAL_ADDED",
      text: `Seal added with barcode ${barcode}, with status NEW`,
    };
    const updatedHistory = inboundShipment.history
      ? [...inboundShipment.history, newHistory]
      : [newHistory];

    const inboundShipmentToUpdate = {
      id: inboundShipment.id,
      history: updatedHistory,
    };
    await performUpdateInbound(inboundShipmentToUpdate);
    const inboundShipmentToDispatch = {
      ...inboundShipment,
      history: updatedHistory,
      seals: {
        ...inboundShipment.seals,
        items: [...inboundShipment.seals.items, seal],
      },
    };
    dispatch({
      type: "SET_INBOUND_SHIPMENT",
      payload: inboundShipmentToDispatch,
    });
  } catch (error) {
    logError("createNewSeal", error, notify);
    throw error;
  }
};

const shouldFetchInboundShipment = ({
  inboundShipmentId,
  isTrackArea,
  lineItemInventoryItem,
  trackInboundSeals,
  verifyInboundRailCar,
}) => {
  if (!inboundShipmentId) return false;

  const loadedLineItemIsTrackArea = isTrack(
    lineItemInventoryItem?.inventoryItem?.location?.area,
  );

  return !!(
    loadedLineItemIsTrackArea ||
    isTrackArea ||
    trackInboundSeals ||
    verifyInboundRailCar
  );
};

const getRailcarVerificationResetState = () => ({
  sourceRailcarInput: "",
  sourceRailcarError: false,
  targetRailcarInput: "",
  targetRailcarError: false,
});

const getTrackRailcarVerificationConfig = ({
  isTrackArea,
  inboundShipmentCarrierType,
  verifyInboundRailCar,
  expectedSourceRailCarNumber,
  outboundShipmentCarrierType,
  verifyOutboundRailCar,
  expectedTargetRailCarNumber,
  isSourceRailcarValidated = false,
  isTargetRailcarValidated = false,
}) => {
  const requiresSourceVerification =
    isTrackArea &&
    inboundShipmentCarrierType === CarrierType.RAIL &&
    verifyInboundRailCar === true &&
    Boolean(normalizeRailCarNumber(expectedSourceRailCarNumber));
  const requiresTargetVerification =
    isTrackArea &&
    outboundShipmentCarrierType === CarrierType.RAIL &&
    verifyOutboundRailCar === true &&
    Boolean(normalizeRailCarNumber(expectedTargetRailCarNumber));

  if (requiresSourceVerification && !isSourceRailcarValidated) {
    return {
      type: "supplying",
      hideSelectedRailcar: true,
    };
  }

  if (requiresTargetVerification && !isTargetRailcarValidated) {
    return {
      type: "receiving",
      hideSelectedRailcar: true,
    };
  }

  return {
    type: null,
    hideSelectedRailcar: false,
  };
};

const getShipmentProcessingBlockedMessage = (trackRailcarVerificationType) => {
  if (trackRailcarVerificationType === "supplying") {
    return "Validate the supplying railcar before processing the shipment.";
  }

  if (trackRailcarVerificationType === "receiving") {
    return "Validate the receiving railcar before processing the shipment.";
  }

  return "";
};

const getTrackRailcarHighlightShipmentId = ({
  trackRailcarVerificationType,
  sourceShipmentId,
  sourceTrackId,
  targetShipmentId,
  targetTrackId,
  selectedTrackId,
  isSourceRailcarValidated = false,
}) => {
  if (trackRailcarVerificationType === "supplying") {
    return sourceShipmentId || targetShipmentId || null;
  }

  if (trackRailcarVerificationType === "receiving") {
    return targetShipmentId || sourceShipmentId || null;
  }

  if (selectedTrackId && selectedTrackId === targetTrackId) {
    return targetShipmentId || sourceShipmentId || null;
  }

  if (selectedTrackId && selectedTrackId === sourceTrackId) {
    return sourceShipmentId || targetShipmentId || null;
  }

  if (trackRailcarVerificationType === null && isSourceRailcarValidated) {
    return sourceShipmentId || targetShipmentId || null;
  }

  return targetShipmentId || sourceShipmentId || null;
};

/** TM track context: only {@link TrackCar} (`location`, `locationFullName`, `trackId`). */
const getShipmentSelectedTrack = ({ trackCar }) => {
  const alignedLocation = getAlignedTrackCarLocation(trackCar);
  if (alignedLocation) {
    return alignedLocation;
  }

  if (trackCar?.trackId) {
    return {
      id: trackCar.trackId,
      locationFullName: trackCar.locationFullName,
    };
  }

  return null;
};

export {
  revalidateSeal,
  breakSeal,
  createNewSeal,
  deActivateSeal,
  reActivateSeal,
  getRailcarVerificationResetState,
  getShipmentProcessingBlockedMessage,
  getShipmentSelectedTrack,
  getTrackRailcarHighlightShipmentId,
  getTrackRailcarVerificationConfig,
  normalizeRailCarNumber,
  shouldFetchInboundShipment,
};
